import React, { useState, useEffect, useMemo } from 'react';
import { ProjectPPE } from '../types/ppe';
import { Project as BackendProject, projectApi } from '../services/projectApi';
import { employeeApi } from '../services/employeeApi';
import { ppeApi, CreatePPEDto } from '../services/ppeApi';
import { Employee } from '../types/employee';
import { useAuth } from '../contexts/AuthContext';
import { useAlert } from '../contexts/AlertContext';

interface NewProjectModalProps {
    isOpen: boolean;
    onClose: () => void;
    project?: BackendProject;
    ppeData?: ProjectPPE;
    onSave: (projectData: any) => Promise<void>;
    readOnly?: boolean; // 읽기 전용 모드
}

// --- Helper Functions ---
const formatCurrency = (value: number) => {
    if (isNaN(value)) return '-';
    return new Intl.NumberFormat('ko-KR').format(Math.round(value));
};

const formatPercentage = (value: number) => {
    if (isNaN(value)) return '0.0%';
    return `${(value * 100).toFixed(1)}%`;
};

// Helper function to round up to millions place (백만의 자리에서 올림) - Excel ROUNDUP(-6) equivalent
const formatRoundUpToMillions = (value: number | string | undefined): number => {
    const numValue = Number(value);
    if (isNaN(numValue) || numValue === 0) return 0;
    return Math.ceil(numValue / 1000000) * 1000000; // Round up to nearest million
};

// --- Initial State for new project ---
const createInitialState = () => ({
    projectName: '',
    period: '',
    months: 1,
    costRatio: 0,
    ecm: 0,
    reserved: 0,
    costOfFinanceRate: 0.1, // 10% (기본값)
    projectManagementRate: 0.03, // 3% (기본값)
    // Revenue by Schedule (입금 일정)
    revenue: {
        items: [
            { name: '착수금', ratio: 0, amount: 0, dueDate: '' },
            { name: '중도금', ratio: 0, amount: 0, dueDate: '' },
            { name: '잔금', ratio: 0, amount: 0, dueDate: '' },
        ],
    },
    // Revenue by Consultant (컨설턴트별 매출) - 투입인건비와 연동
    revenueByConsultant: {
        items: [] as Array<{id?: string, name: string, employeeId?: number | null, mm: number, salesUnitPrice: number, amount: number, unitCost?: number, note?: string, monthlyInputs?: number[]}>,
    },
    // CoGs (Cost of Goods Sold)
    cogs: {
        items: [
            { name: 'Sales Brokerage Fee', amount: 0, monthlyInputs: Array(12).fill(0) },
            { name: '프리랜서 고급', unitPrice: 1800, mm: 0, amount: 0, monthlyInputs: Array(12).fill(0) },
            { name: '프리랜서 중급', unitPrice: 1500, mm: 0, amount: 0, monthlyInputs: Array(12).fill(0) },
        ],
    },
    manpowerCost: {
        items: [] as Array<{name: string, employeeId?: number | null, mm: number, amount: number, unitCost?: number, monthlyInputs?: number[], revenueByConsultantId?: string}>,
    },
    // SG&A - Indirect Opex
    indirectOpex: {
        ratio: 0,
        items: [
            { name: 'Rent & Overhead', amount: 0 },
            { name: 'Cost of Finance (10%)', amount: 0 },
        ],
    },
    // SG&A - Direct Opex
    directOpex: {
        ratio: 0,
        items: [
            { name: 'Project management (3%)', amount: 0 },
            { name: 'ENS', amount: 0 },
            { name: 'Print & Paper', amount: 0 },
            { name: 'Conference', amount: 0 },
            { name: 'Travel for business', amount: 0 },
        ],
    },
});

// --- Components ---
const EditableCell = ({ value, onChange, placeholder, isNumber = false, textAlign = 'right', readOnly = false }: {
    value: any;
    onChange: (val: any) => void;
    placeholder?: string;
    isNumber?: boolean;
    textAlign?: 'left' | 'right' | 'center';
    readOnly?: boolean;
}) => {
    const [isEditing, setIsEditing] = useState(false);
    const [currentValue, setCurrentValue] = useState(value);

    // value prop이 변경될 때 currentValue도 업데이트
    useEffect(() => {
        setCurrentValue(value);
    }, [value]);

    const displayValue = isNumber ? formatCurrency(Number(value)) : value;

    const getTextAlignClass = (align: string) => {
        switch (align) {
            case 'left': return 'text-left';
            case 'center': return 'text-center';
            case 'right': return 'text-right';
            default: return 'text-right';
        }
    };

    // 읽기 전용 모드일 때는 텍스트만 표시
    if (readOnly) {
        return (
            <span className={`px-2 py-1 min-h-[20px] inline-block w-full ${getTextAlignClass(textAlign)}`}>
                {value ? displayValue : <span className="text-gray-400">{placeholder || "-"}</span>}
            </span>
        );
    }

    return isEditing ? (
        <input
            type={isNumber ? "number" : "text"}
            value={currentValue}
            onChange={(e) => setCurrentValue(e.target.value)}
            onBlur={() => { setIsEditing(false); onChange(currentValue); }}
            onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
            placeholder={placeholder || "입력"}
            className={`w-full bg-white border border-indigo-300 rounded px-2 py-1 text-xs ${getTextAlignClass(textAlign)} [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
            autoFocus
        />
    ) : (
        <span onClick={() => setIsEditing(true)} className={`cursor-pointer hover:bg-gray-100 rounded px-2 py-1 min-h-[20px] inline-block w-full ${getTextAlignClass(textAlign)}`}>
            {value ? displayValue : <span className="text-gray-400">{placeholder || "입력"}</span>}
        </span>
    );
};

const FreelancerInputModal = ({ isOpen, onClose, freelancerItems, onSave, startDate, endDate, freelancerName }: {
    isOpen: boolean;
    onClose: () => void;
    freelancerItems: Array<{name: string, unitPrice?: number, mm?: number, amount: number, monthlyInputs: number[]}>;
    onSave: (items: Array<{name: string, amount: number, monthlyInputs: number[]}>) => void;
    startDate?: string;
    endDate?: string;
    freelancerName?: string;
}) => {
    // 연도별 월별 데이터
    const [yearlyInputs, setYearlyInputs] = useState<Record<number, number[]>>({});
    const [selectedCells, setSelectedCells] = useState<Set<string>>(new Set());
    const [isSelecting, setIsSelecting] = useState(false);
    const [bulkValue, setBulkValue] = useState<string>('1.0');
    const [lastClickedCell, setLastClickedCell] = useState<{year: number, month: number} | null>(null);

    // 프로젝트 시작/종료 연월 파싱
    const projectStart = startDate ? new Date(startDate) : new Date();
    const projectEnd = endDate ? new Date(endDate) : new Date();
    const startYear = projectStart.getFullYear();
    const startMonth = projectStart.getMonth();
    const endYear = projectEnd.getFullYear();
    const endMonth = projectEnd.getMonth();

    // 연도 목록 생성
    const years = useMemo(() => {
        const result: number[] = [];
        for (let y = startYear; y <= endYear; y++) {
            result.push(y);
        }
        return result;
    }, [startYear, endYear]);

    // 해당 연도/월이 프로젝트 기간 내인지 확인
    const isInProjectPeriod = (year: number, month: number): boolean => {
        const cellDate = new Date(year, month, 1);
        const start = new Date(startYear, startMonth, 1);
        const end = new Date(endYear, endMonth, 1);
        return cellDate >= start && cellDate <= end;
    };

    // 초기화
    useEffect(() => {
        if (isOpen && freelancerItems) {
            const targetItem = freelancerItems.find(item => item.name === freelancerName);
            const existingInputs = targetItem?.monthlyInputs || [];
            const newYearlyInputs: Record<number, number[]> = {};

            let inputIndex = 0;
            for (let year = startYear; year <= endYear; year++) {
                newYearlyInputs[year] = Array(12).fill(0);
                for (let month = 0; month < 12; month++) {
                    if (isInProjectPeriod(year, month)) {
                        newYearlyInputs[year][month] = existingInputs[inputIndex] || 0;
                        inputIndex++;
                    }
                }
            }
            setYearlyInputs(newYearlyInputs);
            setSelectedCells(new Set());
        }
    }, [freelancerItems, isOpen, startYear, endYear, startMonth, endMonth, freelancerName]);

    if (!isOpen) return null;

    const getCellKey = (year: number, month: number) => `${year}-${month}`;

    const getYearSubtotal = (year: number): number => {
        return (yearlyInputs[year] || []).reduce((sum, val, month) => {
            if (isInProjectPeriod(year, month)) {
                return sum + (Number(val) || 0);
            }
            return sum;
        }, 0);
    };

    const totalMM = years.reduce((sum, year) => sum + getYearSubtotal(year), 0);

    const handleCellChange = (year: number, month: number, value: number) => {
        setYearlyInputs(prev => ({
            ...prev,
            [year]: prev[year].map((v, i) => i === month ? value : v)
        }));
    };

    const handleCellClick = (year: number, month: number, e: React.MouseEvent) => {
        if (!isInProjectPeriod(year, month)) return;
        const key = getCellKey(year, month);

        if (e.shiftKey && lastClickedCell) {
            const newSelected = new Set(Array.from(selectedCells));
            const sYear = Math.min(lastClickedCell.year, year);
            const eYear = Math.max(lastClickedCell.year, year);
            const sMonth = Math.min(lastClickedCell.month, month);
            const eMonth = Math.max(lastClickedCell.month, month);

            for (let y = sYear; y <= eYear; y++) {
                for (let m = sMonth; m <= eMonth; m++) {
                    if (isInProjectPeriod(y, m)) {
                        newSelected.add(getCellKey(y, m));
                    }
                }
            }
            setSelectedCells(newSelected);
        } else if (e.ctrlKey || e.metaKey) {
            const newSelected = new Set(Array.from(selectedCells));
            if (newSelected.has(key)) {
                newSelected.delete(key);
            } else {
                newSelected.add(key);
            }
            setSelectedCells(newSelected);
            setLastClickedCell({ year, month });
        } else {
            setSelectedCells(new Set([key]));
            setLastClickedCell({ year, month });
        }
    };

    const handleMouseDown = (year: number, month: number, e: React.MouseEvent) => {
        if (!isInProjectPeriod(year, month)) return;
        setIsSelecting(true);
        if (!e.shiftKey && !e.ctrlKey && !e.metaKey) {
            const key = getCellKey(year, month);
            setSelectedCells(new Set([key]));
            setLastClickedCell({ year, month });
        }
    };

    const handleMouseEnter = (year: number, month: number) => {
        if (!isSelecting || !isInProjectPeriod(year, month)) return;
        const key = getCellKey(year, month);
        setSelectedCells(prev => {
            const newSet = new Set(Array.from(prev));
            newSet.add(key);
            return newSet;
        });
    };

    const handleMouseUp = () => {
        setIsSelecting(false);
    };

    const applyBulkValue = () => {
        const value = Number(bulkValue) || 0;
        const newYearlyInputs = { ...yearlyInputs };

        selectedCells.forEach(key => {
            const [yearStr, monthStr] = key.split('-');
            const year = Number(yearStr);
            const month = Number(monthStr);
            if (newYearlyInputs[year]) {
                newYearlyInputs[year] = [...newYearlyInputs[year]];
                newYearlyInputs[year][month] = value;
            }
        });

        setYearlyInputs(newYearlyInputs);
        setSelectedCells(new Set());
    };

    const handleSave = () => {
        const monthlyInputs: number[] = [];
        for (let year = startYear; year <= endYear; year++) {
            for (let month = 0; month < 12; month++) {
                if (isInProjectPeriod(year, month)) {
                    monthlyInputs.push(yearlyInputs[year]?.[month] || 0);
                }
            }
        }

        const updatedItems = freelancerItems.map(item => {
            if (item.name === freelancerName) {
                return {
                    ...item,
                    monthlyInputs,
                    mm: Math.round(totalMM * 100) / 100
                };
            }
            return item;
        });

        onSave(updatedItems);
        onClose();
    };

    const monthNames = ['1월', '2월', '3월', '4월', '5월', '6월', '7월', '8월', '9월', '10월', '11월', '12월'];
    const targetItem = freelancerItems.find(item => item.name === freelancerName);
    const unitPrice = targetItem?.unitPrice || (freelancerName?.includes('고급') ? 1800 : 1500);

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50" onMouseUp={handleMouseUp}>
            <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-5xl max-h-[90vh] overflow-auto">
                {/* 헤더 */}
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-lg font-bold">{freelancerName || '프리랜서'} 투입 경과</h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl">&times;</button>
                </div>

                {/* 요약 정보 */}
                <div className="flex gap-6 mb-4 p-3 bg-slate-50 rounded-lg">
                    <div className="text-sm">
                        <span className="text-gray-500">단가</span>
                        <span className="ml-2 font-semibold">{unitPrice?.toLocaleString() || 0}만원/MM</span>
                    </div>
                    <div className="text-sm">
                        <span className="text-gray-500">합계</span>
                        <span className="ml-2 font-semibold text-blue-600">{totalMM.toFixed(1)} MM</span>
                    </div>
                    <div className="text-sm">
                        <span className="text-gray-500">총 금액</span>
                        <span className="ml-2 font-semibold text-green-600">
                            {Math.round(totalMM * unitPrice).toLocaleString()}만원
                        </span>
                    </div>
                </div>

                {/* 일괄 입력 */}
                <div className="flex items-center gap-3 mb-4 p-3 bg-blue-50 rounded-lg">
                    <span className="text-sm text-blue-700">일괄입력:</span>
                    <input
                        type="number"
                        step="0.1"
                        value={bulkValue}
                        onChange={(e) => setBulkValue(e.target.value)}
                        className="w-20 px-2 py-1 text-center text-sm border border-blue-300 rounded"
                    />
                    <button
                        onClick={applyBulkValue}
                        disabled={selectedCells.size === 0}
                        className={`px-3 py-1 text-sm rounded ${
                            selectedCells.size > 0
                                ? 'bg-blue-500 text-white hover:bg-blue-600'
                                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                        }`}
                    >
                        선택영역에 적용 ({selectedCells.size}개)
                    </button>
                    <span className="text-xs text-blue-600 ml-2">셀 클릭/드래그</span>
                </div>

                {/* 테이블 */}
                <div className="overflow-x-auto">
                    <table className="w-full text-sm border-collapse select-none">
                        <thead>
                            <tr className="bg-gray-100">
                                <th className="p-2 border text-center w-16">연도</th>
                                {monthNames.map((name, i) => (
                                    <th key={i} className="p-1 border text-center text-xs w-12">{name}</th>
                                ))}
                                <th className="p-2 border text-center text-xs font-bold w-16">소계</th>
                            </tr>
                        </thead>
                        <tbody>
                            {years.map(year => (
                                <tr key={year}>
                                    <td className="p-2 border text-center font-semibold bg-gray-50">{year}</td>
                                    {monthNames.map((_, month) => {
                                        const inPeriod = isInProjectPeriod(year, month);
                                        const cellKey = getCellKey(year, month);
                                        const isSelected = selectedCells.has(cellKey);

                                        return (
                                            <td
                                                key={month}
                                                className={`p-1 border cursor-pointer ${
                                                    !inPeriod ? 'bg-gray-200 cursor-not-allowed' :
                                                    isSelected ? 'bg-blue-200' : 'bg-white hover:bg-blue-50'
                                                }`}
                                                onMouseDown={(e) => handleMouseDown(year, month, e)}
                                                onMouseEnter={() => handleMouseEnter(year, month)}
                                                onClick={(e) => handleCellClick(year, month, e)}
                                            >
                                                {inPeriod ? (
                                                    <input
                                                        type="number"
                                                        step="0.1"
                                                        min="0"
                                                        value={yearlyInputs[year]?.[month] || 0}
                                                        onChange={(e) => {
                                                            const val = e.target.value === '' ? 0 : Number(e.target.value);
                                                            handleCellChange(year, month, val);
                                                        }}
                                                        onClick={(e) => e.stopPropagation()}
                                                        className={`w-full h-7 text-center text-xs border rounded cursor-text ${
                                                            isSelected ? 'bg-blue-100 border-blue-300' : 'bg-white border-gray-200'
                                                        } focus:border-blue-400 focus:ring-1 focus:ring-blue-200 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
                                                    />
                                                ) : (
                                                    <span className="block h-7 text-center text-gray-400">-</span>
                                                )}
                                            </td>
                                        );
                                    })}
                                    <td className="p-2 border text-center font-semibold bg-blue-50 text-blue-700">
                                        {getYearSubtotal(year).toFixed(1)}
                                    </td>
                                </tr>
                            ))}
                            {/* 합계 행 */}
                            <tr className="bg-slate-100 font-bold">
                                <td className="p-2 border text-center">합계</td>
                                <td colSpan={12} className="p-2 border"></td>
                                <td className="p-2 border text-center text-blue-700">{totalMM.toFixed(1)}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                {/* 버튼 */}
                <div className="flex justify-end gap-2 mt-4">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-semibold text-slate-600 bg-white rounded-lg hover:bg-slate-50"
                    >
                        취소
                    </button>
                    <button
                        onClick={handleSave}
                        className="px-4 py-2 text-sm font-semibold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700"
                    >
                        저장
                    </button>
                </div>
            </div>
        </div>
    );
};

// 내부 직원 투입 경과 모달 (연도별 행 분리 + 드래그 선택)
const ManpowerInputModal = ({ isOpen, onClose, employeeName, employeeData, onSave, startDate, endDate }: {
    isOpen: boolean;
    onClose: () => void;
    employeeName: string;
    employeeData: { name: string, employeeId?: number | null, mm: number, unitCost: number, monthlyInputs?: number[] };
    onSave: (updatedData: { mm: number, monthlyInputs: number[] }) => void;
    startDate?: string;
    endDate?: string;
}) => {
    // 연도별 월별 데이터: { 2024: [0,0,0,0,0,0.5,1.0,...], 2025: [...] }
    const [yearlyInputs, setYearlyInputs] = useState<Record<number, number[]>>({});
    const [selectedCells, setSelectedCells] = useState<Set<string>>(new Set());
    const [isSelecting, setIsSelecting] = useState(false);
    const [bulkValue, setBulkValue] = useState<string>('1.0');
    const [lastClickedCell, setLastClickedCell] = useState<{year: number, month: number} | null>(null);

    // 프로젝트 시작/종료 연월 파싱
    const projectStart = startDate ? new Date(startDate) : new Date();
    const projectEnd = endDate ? new Date(endDate) : new Date();
    const startYear = projectStart.getFullYear();
    const startMonth = projectStart.getMonth(); // 0-based
    const endYear = projectEnd.getFullYear();
    const endMonth = projectEnd.getMonth(); // 0-based

    // 연도 목록 생성
    const years = useMemo(() => {
        const result: number[] = [];
        for (let y = startYear; y <= endYear; y++) {
            result.push(y);
        }
        return result;
    }, [startYear, endYear]);

    // 해당 연도/월이 프로젝트 기간 내인지 확인
    const isInProjectPeriod = (year: number, month: number): boolean => {
        const cellDate = new Date(year, month, 1);
        const start = new Date(startYear, startMonth, 1);
        const end = new Date(endYear, endMonth, 1);
        return cellDate >= start && cellDate <= end;
    };

    // 초기화: monthlyInputs를 연도별로 분리
    useEffect(() => {
        if (isOpen && employeeData) {
            const existingInputs = employeeData.monthlyInputs || [];
            const newYearlyInputs: Record<number, number[]> = {};

            let inputIndex = 0;
            for (let year = startYear; year <= endYear; year++) {
                newYearlyInputs[year] = Array(12).fill(0);
                for (let month = 0; month < 12; month++) {
                    if (isInProjectPeriod(year, month)) {
                        newYearlyInputs[year][month] = existingInputs[inputIndex] || 0;
                        inputIndex++;
                    }
                }
            }
            setYearlyInputs(newYearlyInputs);
            setSelectedCells(new Set());
        }
    }, [employeeData, isOpen, startYear, endYear, startMonth, endMonth]);

    if (!isOpen) return null;

    // 셀 키 생성
    const getCellKey = (year: number, month: number) => `${year}-${month}`;

    // 연도별 소계 계산
    const getYearSubtotal = (year: number): number => {
        return (yearlyInputs[year] || []).reduce((sum, val, month) => {
            if (isInProjectPeriod(year, month)) {
                return sum + (Number(val) || 0);
            }
            return sum;
        }, 0);
    };

    // 전체 합계 계산
    const totalMM = years.reduce((sum, year) => sum + getYearSubtotal(year), 0);

    // 셀 값 변경
    const handleCellChange = (year: number, month: number, value: number) => {
        setYearlyInputs(prev => ({
            ...prev,
            [year]: prev[year].map((v, i) => i === month ? value : v)
        }));
    };

    const handleCellClick = (year: number, month: number, e: React.MouseEvent) => {
        if (!isInProjectPeriod(year, month)) return;

        const key = getCellKey(year, month);

        if (e.shiftKey && lastClickedCell) {
            // Shift+클릭: 범위 선택
            const newSelected = new Set(Array.from(selectedCells));

            const startYear = Math.min(lastClickedCell.year, year);
            const endYear = Math.max(lastClickedCell.year, year);
            const startMonth = Math.min(lastClickedCell.month, month);
            const endMonth = Math.max(lastClickedCell.month, month);

            for (let y = startYear; y <= endYear; y++) {
                for (let m = startMonth; m <= endMonth; m++) {
                    if (isInProjectPeriod(y, m)) {
                        newSelected.add(getCellKey(y, m));
                    }
                }
            }
            setSelectedCells(newSelected);
        } else if (e.ctrlKey || e.metaKey) {
            // Ctrl/Cmd+클릭: 토글
            const newSelected = new Set(Array.from(selectedCells));
            if (newSelected.has(key)) {
                newSelected.delete(key);
            } else {
                newSelected.add(key);
            }
            setSelectedCells(newSelected);
            setLastClickedCell({ year, month });
        } else {
            // 일반 클릭: 단일 선택
            setSelectedCells(new Set([key]));
            setLastClickedCell({ year, month });
        }
    };

    // 드래그 시작
    const handleMouseDown = (year: number, month: number, e: React.MouseEvent) => {
        if (!isInProjectPeriod(year, month)) return;
        // 드래그 선택 모드 시작
        setIsSelecting(true);
        if (!e.shiftKey && !e.ctrlKey && !e.metaKey) {
            const key = getCellKey(year, month);
            setSelectedCells(new Set([key]));
            setLastClickedCell({ year, month });
        }
    };

    // 드래그 중
    const handleMouseEnter = (year: number, month: number) => {
        if (!isSelecting || !isInProjectPeriod(year, month)) return;
        const key = getCellKey(year, month);
        setSelectedCells(prev => {
            const newSet = new Set(Array.from(prev));
            newSet.add(key);
            return newSet;
        });
    };

    // 드래그 종료
    const handleMouseUp = () => {
        setIsSelecting(false);
    };

    // 선택 영역에 일괄 적용
    const applyBulkValue = () => {
        const value = Number(bulkValue) || 0;
        const newYearlyInputs = { ...yearlyInputs };

        selectedCells.forEach(key => {
            const [yearStr, monthStr] = key.split('-');
            const year = Number(yearStr);
            const month = Number(monthStr);
            if (newYearlyInputs[year]) {
                newYearlyInputs[year] = [...newYearlyInputs[year]];
                newYearlyInputs[year][month] = value;
            }
        });

        setYearlyInputs(newYearlyInputs);
        setSelectedCells(new Set());
    };

    // 저장 시 monthlyInputs 배열로 변환
    const handleSave = () => {
        const monthlyInputs: number[] = [];
        for (let year = startYear; year <= endYear; year++) {
            for (let month = 0; month < 12; month++) {
                if (isInProjectPeriod(year, month)) {
                    monthlyInputs.push(yearlyInputs[year]?.[month] || 0);
                }
            }
        }
        onSave({
            mm: Math.round(totalMM * 100) / 100,
            monthlyInputs
        });
        onClose();
    };

    const monthNames = ['1월', '2월', '3월', '4월', '5월', '6월', '7월', '8월', '9월', '10월', '11월', '12월'];

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50" onMouseUp={handleMouseUp}>
            <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-5xl max-h-[90vh] overflow-auto">
                {/* 헤더 */}
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-lg font-bold">{employeeName} 투입 경과</h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl">&times;</button>
                </div>

                {/* 요약 정보 */}
                <div className="flex gap-6 mb-4 p-3 bg-slate-50 rounded-lg">
                    <div className="text-sm">
                        <span className="text-gray-500">인력원가</span>
                        <span className="ml-2 font-semibold">{employeeData.unitCost?.toLocaleString() || 0}만원/MM</span>
                    </div>
                    <div className="text-sm">
                        <span className="text-gray-500">합계</span>
                        <span className="ml-2 font-semibold text-blue-600">{totalMM.toFixed(1)} MM</span>
                    </div>
                    <div className="text-sm">
                        <span className="text-gray-500">총 인건비</span>
                        <span className="ml-2 font-semibold text-green-600">
                            {Math.round(totalMM * (employeeData.unitCost || 0)).toLocaleString()}만원
                        </span>
                    </div>
                </div>

                {/* 일괄 입력 */}
                <div className="flex items-center gap-3 mb-4 p-3 bg-blue-50 rounded-lg">
                    <span className="text-sm text-blue-700">일괄입력:</span>
                    <input
                        type="number"
                        step="0.1"
                        value={bulkValue}
                        onChange={(e) => setBulkValue(e.target.value)}
                        className="w-20 px-2 py-1 text-center text-sm border border-blue-300 rounded"
                    />
                    <button
                        onClick={applyBulkValue}
                        disabled={selectedCells.size === 0}
                        className={`px-3 py-1 text-sm rounded ${
                            selectedCells.size > 0
                                ? 'bg-blue-500 text-white hover:bg-blue-600'
                                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                        }`}
                    >
                        선택영역에 적용 ({selectedCells.size}개)
                    </button>
                    <span className="text-xs text-blue-600 ml-2">셀 클릭/드래그</span>
                </div>

                {/* 테이블 */}
                <div className="overflow-x-auto">
                    <table className="w-full text-sm border-collapse select-none">
                        <thead>
                            <tr className="bg-gray-100">
                                <th className="p-2 border text-center w-16">연도</th>
                                {monthNames.map((name, i) => (
                                    <th key={i} className="p-1 border text-center text-xs w-12">{name}</th>
                                ))}
                                <th className="p-2 border text-center text-xs font-bold w-16">소계</th>
                            </tr>
                        </thead>
                        <tbody>
                            {years.map(year => (
                                <tr key={year}>
                                    <td className="p-2 border text-center font-semibold bg-gray-50">{year}</td>
                                    {monthNames.map((_, month) => {
                                        const inPeriod = isInProjectPeriod(year, month);
                                        const cellKey = getCellKey(year, month);
                                        const isSelected = selectedCells.has(cellKey);

                                        return (
                                            <td
                                                key={month}
                                                className={`p-1 border cursor-pointer ${
                                                    !inPeriod ? 'bg-gray-200 cursor-not-allowed' :
                                                    isSelected ? 'bg-blue-200' : 'bg-white hover:bg-blue-50'
                                                }`}
                                                onMouseDown={(e) => handleMouseDown(year, month, e)}
                                                onMouseEnter={() => handleMouseEnter(year, month)}
                                                onClick={(e) => handleCellClick(year, month, e)}
                                            >
                                                {inPeriod ? (
                                                    <input
                                                        type="number"
                                                        step="0.1"
                                                        min="0"
                                                        value={yearlyInputs[year]?.[month] || 0}
                                                        onChange={(e) => {
                                                            const val = e.target.value === '' ? 0 : Number(e.target.value);
                                                            handleCellChange(year, month, val);
                                                        }}
                                                        onClick={(e) => e.stopPropagation()}
                                                        className={`w-full h-7 text-center text-xs border rounded cursor-text ${
                                                            isSelected ? 'bg-blue-100 border-blue-300' : 'bg-white border-gray-200'
                                                        } focus:border-blue-400 focus:ring-1 focus:ring-blue-200 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
                                                    />
                                                ) : (
                                                    <span className="block h-7 text-center text-gray-400">-</span>
                                                )}
                                            </td>
                                        );
                                    })}
                                    <td className="p-2 border text-center font-semibold bg-blue-50 text-blue-700">
                                        {getYearSubtotal(year).toFixed(1)}
                                    </td>
                                </tr>
                            ))}
                            {/* 합계 행 */}
                            <tr className="bg-slate-100 font-bold">
                                <td className="p-2 border text-center">합계</td>
                                <td colSpan={12} className="p-2 border"></td>
                                <td className="p-2 border text-center text-blue-700">{totalMM.toFixed(1)}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                {/* 버튼 */}
                <div className="flex justify-end gap-2 mt-4">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-semibold text-slate-600 bg-white border border-slate-300 rounded-lg hover:bg-slate-50"
                    >
                        취소
                    </button>
                    <button
                        onClick={handleSave}
                        className="px-4 py-2 text-sm font-semibold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700"
                    >
                        저장
                    </button>
                </div>
            </div>
        </div>
    );
};

const NewProjectModal: React.FC<NewProjectModalProps> = ({
  isOpen,
  onClose,
  project,
  onSave,
  readOnly = false
}) => {
    const { userRole } = useAuth();
    const { showToast } = useAlert();
    const [projectData, setProjectData] = useState(createInitialState());
    const [isFreelancerModalOpen, setIsFreelancerModalOpen] = useState(false);
    const [selectedFreelancerIndex, setSelectedFreelancerIndex] = useState<number>(-1);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [employees, setEmployees] = useState<Employee[]>([]);
    const [selectedPM, setSelectedPM] = useState<string>('');
    // ManpowerInputModal 상태
    const [isManpowerModalOpen, setIsManpowerModalOpen] = useState(false);
    const [selectedManpowerEmployeeIndex, setSelectedManpowerEmployeeIndex] = useState<number>(-1);
    const [isManpowerForRevenueByConsultant, setIsManpowerForRevenueByConsultant] = useState(false);

    useEffect(() => {
        if (project) {
            const start = new Date(project.startDate).toISOString().split('T')[0];
            const end = new Date(project.endDate).toISOString().split('T')[0];
            const months = calculateMonths(start, end);
            setStartDate(start);
            setEndDate(end);
            setProjectData(prev => ({
                ...prev,
                projectName: project.name,
                period: `${start} ~ ${end}`,
                months: months
            }));
            // employees가 로드된 후에 PM 설정 (employees 의존성에서 처리)

            // 기존 프로젝트의 PPE 데이터 로드
            const loadPPEData = async () => {
                try {
                    const ppeData = await ppeApi.getByProjectId(project.id);
                    if (ppeData && ppeData.revenueItems) {
                        const revenueItems = Array.isArray(ppeData.revenueItems) && ppeData.revenueItems.length > 0
                            ? ppeData.revenueItems.map((item: any) => ({
                                name: item.name,
                                ratio: item.ratio || 0,
                                amount: item.amount || 0,
                                dueDate: item.dueDate || ''
                        })) : [{ name: '착수금', ratio: 0, amount: 0, dueDate: '' }]; // 최소 1개 항목 보장

                        setProjectData(prev => ({
                            ...prev,
                            costOfFinanceRate: ppeData.costOfFinanceRate !== null && ppeData.costOfFinanceRate !== undefined ? ppeData.costOfFinanceRate : 0.1, // 기존 데이터에서 비율 복원
                            projectManagementRate: ppeData.projectManagementRate !== null && ppeData.projectManagementRate !== undefined ? ppeData.projectManagementRate : 0.03, // 기존 데이터에서 비율 복원
                            revenue: {
                                items: revenueItems
                            },
                            cogs: {
                                items: ppeData.grossCostItems || prev.cogs.items
                            },
                            manpowerCost: {
                                items: ppeData.manpowerItems || prev.manpowerCost.items
                            },
                            indirectOpex: {
                                ratio: prev.indirectOpex.ratio,
                                items: ppeData.indirectOpexItems || prev.indirectOpex.items
                            },
                            directOpex: {
                                ratio: prev.directOpex.ratio,
                                items: ppeData.directOpexItems || prev.directOpex.items
                            },
                            revenueByConsultant: {
                                items: ppeData.revenueByConsultantItems || prev.revenueByConsultant.items
                            }
                        }));
                    }
                } catch (error) {
                }
            };
            
            loadPPEData();
        } else {
            // 새 프로젝트인 경우 초기 상태로 리셋
            setProjectData(createInitialState());
            setStartDate('');
            setEndDate('');
            setSelectedPM('');
        }
    }, [project]);

    // 직원 목록 가져오기 (인력원가 테이블과 동일한 데이터)
    useEffect(() => {
        const fetchEmployees = async () => {
            if (isOpen) {
                try {
                    // 프로젝트 기간에 걸친 모든 연도 추출
                    const projectStartDate = project?.startDate ? new Date(project.startDate) : (startDate ? new Date(startDate) : new Date());
                    const projectEndDate = project?.endDate ? new Date(project.endDate) : (endDate ? new Date(endDate) : new Date());

                    const startYear = projectStartDate.getFullYear();
                    const endYear = projectEndDate.getFullYear();

                    // 프로젝트 기간에 포함된 모든 연도의 인력원가 가져오기
                    const allYears: number[] = [];
                    for (let year = startYear; year <= endYear; year++) {
                        allYears.push(year);
                    }

                    // 각 연도의 인력원가 데이터를 모두 가져오기
                    const hrCostByYear: Record<number, any[]> = {};
                    for (const year of allYears) {
                        try {
                            const yearData = await employeeApi.getAllHRCostByYear(year);
                            hrCostByYear[year] = yearData;
                        } catch (error) {
                            hrCostByYear[year] = [];
                        }
                    }

                    // 인력원가 계산 함수 (프로젝트 시작 연도 기준)
                    const calculateUnitCost = (employeeId: number): number => {
                        // 프로젝트 시작 연도의 인력원가 사용
                        const yearData = hrCostByYear[startYear];
                        const employeeData = yearData?.find((item: any) =>
                            (item.employee?.id || -(item.hrCost.id)) === employeeId
                        );
                        if (employeeData) {
                            const finalLaborCost = employeeData.hrCost.adjustedFinalLaborCost || employeeData.hrCost.finalLaborCost;
                            const rounded = formatRoundUpToMillions(finalLaborCost);
                            const unitCost = Math.round(rounded / 10000);
                            return unitCost;
                        }
                        return 0;
                    };

                    // 첫 번째 연도의 직원 목록을 기준으로 Employee 데이터 생성
                    const baseYearData = hrCostByYear[startYear] || [];
                    const hrEmployees = baseYearData.map((item: any) => {
                        const employeeId = item.employee?.id || -(item.hrCost.id);
                        return {
                            id: employeeId,
                            empNo: item.employee?.empNo || `VIRTUAL-${item.hrCost.id}`,
                            name: item.employee?.name || item.hrCost.memo || 'Unknown',
                            email: item.employee?.email || '',
                            position: item.employee?.position || item.hrCost.memo || 'Unknown',
                            rank: item.employee?.rank || item.employee?.position || item.hrCost.memo || 'Unknown',
                            department: item.employee?.department || 'Virtual',
                            tel: item.employee?.tel || '',
                            joinDate: new Date(item.employee?.joinDate || new Date()),
                            annualSalary: item.employee?.annualSalary || item.hrCost.annualSalary,
                            status: 'ACTIVE' as const,
                            createdAt: new Date(item.employee?.createdAt || new Date()),
                            updatedAt: new Date(item.employee?.updatedAt || new Date()),
                            hrCostData: item.hrCost, // 첫 번째 연도 HR Cost 데이터 포함
                            weightedAvgUnitCost: calculateUnitCost(employeeId) // 프로젝트 시작 연도 인력원가
                        };
                    });

                    setEmployees(hrEmployees);
                } catch (error) {
                    setEmployees([]);
                }
            }
        };
        fetchEmployees();
    }, [isOpen, project, startDate, endDate]);

    // employees가 로드되고 project가 있을 때 PM 설정
    useEffect(() => {
        if (project && employees.length > 0) {
            const pmEmployee = employees.find(emp => emp.name === project.pm);
            setSelectedPM(pmEmployee ? pmEmployee.id.toString() : '');
        }
    }, [project, employees]);

    // 기간(월) 계산 함수
    const calculateMonths = (start: string, end: string): number => {
        if (!start || !end) return 1; // 최소 1개월로 설정
        
        const startDate = new Date(start);
        const endDate = new Date(end);
        
        const yearDiff = endDate.getFullYear() - startDate.getFullYear();
        const monthDiff = endDate.getMonth() - startDate.getMonth();
        const dayDiff = endDate.getDate() - startDate.getDate();
        
        let totalMonths = yearDiff * 12 + monthDiff;
        
        // 일수 차이를 월로 환산 (대략적으로 30일을 1월로 계산)
        const dayRatio = dayDiff / 30;
        totalMonths += dayRatio;
        
        const result = Math.round(totalMonths * 10) / 10; // 소수점 첫째자리까지
        return Math.max(result, 1); // 최소 1개월 보장
    };

    // 날짜 변경 핸들러
    const handleDateChange = (type: 'start' | 'end', value: string) => {
        if (type === 'start') {
            setStartDate(value);
            const period = value && endDate ? `${value} ~ ${endDate}` : '';
            const months = calculateMonths(value, endDate);
            
            setProjectData(prev => ({
                ...prev,
                period: period,
                months: months
            }));
        } else {
            setEndDate(value);
            const period = startDate && value ? `${startDate} ~ ${value}` : '';
            const months = calculateMonths(startDate, value);
            
            setProjectData(prev => ({
                ...prev,
                period: period,
                months: months
            }));
        }
    };

    const totals = useMemo(() => {
        // Cost of Finance 비율 (기본값 0.1 = 10%, null/undefined일 때만 기본값 사용)
        const costOfFinanceRate = projectData.costOfFinanceRate !== null && projectData.costOfFinanceRate !== undefined
            ? projectData.costOfFinanceRate
            : 0.1;
        // Project Management 비율 (기본값 0.03 = 3%, null/undefined일 때만 기본값 사용)
        const projectManagementRate = projectData.projectManagementRate !== null && projectData.projectManagementRate !== undefined
            ? projectData.projectManagementRate
            : 0.03;

        const revenueBySchedule = projectData.revenue.items.reduce((sum, item) => sum + (Number(item.amount) || 0), 0);
        // by Consultant는 Revenue 합계에 포함하지 않음 (CR 계산에만 사용)
        const revenue = revenueBySchedule;
        const cogs = projectData.cogs.items.reduce((sum, item) => {
            // 프리랜서 항목인 경우 monthlyInputs에서 계산
            const isFreelancerSenior = item.name.includes('프리랜서 고급');
            const isFreelancerMiddle = item.name.includes('프리랜서 중급');
            const isFreelancer = isFreelancerSenior || isFreelancerMiddle;

            if (isFreelancer && (item as any).monthlyInputs && Array.isArray((item as any).monthlyInputs)) {
                const mmValue = (item as any).monthlyInputs.reduce((s: number, val: number) => s + (Number(val) || 0), 0);
                const unitPrice = (item as any).unitPrice || (isFreelancerSenior ? 1800 : 1500);
                const amount = Math.round(mmValue * unitPrice);
                return sum + amount;
            }

            return sum + (Number(item.amount) || 0);
        }, 0);
        const manpowerCost = projectData.manpowerCost.items.reduce((sum, item) => {
            const unitCost = item.unitCost || 0;
            const mm = item.mm || 0;
            return sum + Math.round(mm * unitCost);
        }, 0);
        const indirectOpex = projectData.indirectOpex.items.reduce((sum, item, index) => {
            if (item.name.includes('Cost of Finance')) {
                // Cost of Finance는 투입인건비 총합 * costOfFinanceRate로 자동 계산
                return sum + Math.round(manpowerCost * costOfFinanceRate);
            }
            return sum + (Number(item.amount) || 0);
        }, 0);
        const directOpex = projectData.directOpex.items.reduce((sum, item) => {
            if (item.name.includes('Project management')) {
                // Project management는 Revenue * projectManagementRate로 자동 계산
                return sum + Math.round(revenue * projectManagementRate);
            }
            return sum + (Number(item.amount) || 0);
        }, 0);

        // Indirect Opex 퍼센트 합계 계산
        const indirectOpexPercentage = projectData.indirectOpex.items.reduce((sum, item) => {
            if (item.name.includes('Cost of Finance')) {
                return sum + costOfFinanceRate;
            }
            return sum + ((Number(item.amount) || 0) / (revenue || 1));
        }, 0);

        // Direct Opex 퍼센트 합계 계산
        const directOpexPercentage = projectData.directOpex.items.reduce((sum, item) => {
            if (item.name.includes('Project management')) {
                return sum + projectManagementRate; // 동적 비율
            }
            return sum + ((Number(item.amount) || 0) / (revenue || 1));
        }, 0);

        const grossMargin = revenue - cogs - manpowerCost;  // Revenue - CoGs (투입인건비 + 프리랜서)
        const operationMargin = grossMargin - indirectOpex - directOpex;
        const incomeTax = operationMargin * 0.2;
        const netProfit = operationMargin - incomeTax;

        // ECM = Gross Margin * (0.45/1.45)
        const ecm = Math.round(grossMargin * (0.45/1.45));

        // Cost of Finance = 투입인건비 총합 * costOfFinanceRate
        const costOfFinance = Math.round(manpowerCost * costOfFinanceRate);

        // Reserved = roundup(Cost of Finance + ECM, -2) - 백의 자리에서 올림
        const reserved = Math.ceil((costOfFinance + ecm) / 100) * 100;

        // Project Management 금액 계산
        const projectManagement = Math.round(revenue * projectManagementRate);

        return { revenue, cogs, manpowerCost, indirectOpex, directOpex, indirectOpexPercentage, directOpexPercentage, grossMargin, operationMargin, incomeTax, netProfit, ecm, reserved, costOfFinance, costOfFinanceRate, projectManagement, projectManagementRate };
    }, [projectData]);

    const handleUpdate = (path: (string | number)[], value: any) => {
        setProjectData(prev => {
            const newProject = JSON.parse(JSON.stringify(prev));
            let current: any = newProject;
            for (let i = 0; i < path.length - 1; i++) { 
                current = current[path[i]]; 
            }
            current[path[path.length - 1]] = value;
            return newProject;
        });
    };

    const handleClose = () => {
        onClose();
    };

    const handleSave = async () => {
        // 새 프로젝트인 경우에만 프로젝트명 유효성 검사
        if (!project && (!projectData.projectName || projectData.projectName.trim() === '')) {
            showToast('프로젝트명을 입력해주세요.', 'warning');
            return;
        }

        try {
            // totals에서 계산된 데이터 가져오기
            const calculatedData = totals;

            // CreatePPEDto 형식에 맞게 데이터 구성
            const ppeData: CreatePPEDto = {
                // 기본 입력 데이터
                revenue: calculatedData.revenue,
                grossCost: calculatedData.cogs,
                manpowerCost: calculatedData.manpowerCost,
                indirectOpex: calculatedData.indirectOpex,
                directOpex: calculatedData.directOpex,

                // 계산된 데이터
                grossMargin: calculatedData.grossMargin,
                grossIncome: calculatedData.grossMargin,  // grossMargin과 동일 (CoGs = 투입인건비 + 프리랜서)
                grossIncomeRate: calculatedData.grossMargin / (calculatedData.revenue || 1),
                operationIncome: calculatedData.operationMargin,
                operationIncomeRate: calculatedData.operationMargin / (calculatedData.revenue || 1),
                incomeTax: calculatedData.incomeTax,
                profit: calculatedData.netProfit,
                profitRate: calculatedData.netProfit / (calculatedData.revenue || 1),

                // 추가 계산 데이터
                ecm: calculatedData.ecm,
                costOfFinance: calculatedData.costOfFinance,
                costOfFinanceRate: projectData.costOfFinanceRate !== null && projectData.costOfFinanceRate !== undefined ? projectData.costOfFinanceRate : 0.1,
                projectManagementRate: projectData.projectManagementRate !== null && projectData.projectManagementRate !== undefined ? projectData.projectManagementRate : 0.03,
                reserved: calculatedData.reserved,

                // 프로젝트 기본 정보
                months: Math.round(projectData.months),

                // JSON 필드로 세부 데이터 저장
                revenueItems: projectData.revenue.items,
                grossCostItems: projectData.cogs.items,
                manpowerItems: projectData.manpowerCost.items.map(item => ({
                    ...item,
                    totalAmount: Math.round((item.mm || 0) * (item.unitCost || 0))
                })),
                indirectOpexItems: projectData.indirectOpex.items,
                directOpexItems: projectData.directOpex.items,
                revenueByConsultantItems: projectData.revenueByConsultant.items.map(item => ({
                    ...item,
                    amount: Math.round((item.unitCost || 0) * (item.mm || 0))
                })),
            };

            if (project?.id) {
                // 프로젝트명, PM, 시작일, 종료일이 변경된 경우 프로젝트 업데이트
                const updateData: any = {};
                if (projectData.projectName && projectData.projectName.trim() !== '' && projectData.projectName !== project.name) {
                    updateData.name = projectData.projectName;
                }
                if (selectedPM && selectedPM !== project.pm) {
                    // selectedPM은 이제 Employee ID이므로, 이름과 ID를 함께 전송
                    const selectedEmployee = employees.find(emp => emp.id.toString() === selectedPM);
                    if (selectedEmployee) {
                        updateData.pm = selectedEmployee.name;
                        updateData.pmId = selectedEmployee.id;
                    }
                }
                // 시작일이 변경된 경우
                if (startDate && startDate !== new Date(project.startDate).toISOString().split('T')[0]) {
                    updateData.startDate = startDate;
                }
                // 종료일이 변경된 경우
                if (endDate && endDate !== new Date(project.endDate).toISOString().split('T')[0]) {
                    updateData.endDate = endDate;
                }
                
                if (Object.keys(updateData).length > 0) {
                    try {
                        await projectApi.update(project.id, updateData);
                    } catch (error) {
                        showToast('프로젝트 업데이트 중 오류가 발생했습니다.', 'error');
                        return;
                    }
                }

                // 기존 프로젝트 PPE 데이터 업데이트
                try {
                    const existingPPE = await ppeApi.getByProjectId(project.id);
                    if (existingPPE && existingPPE.id) {
                        // 기존 PPE 데이터 업데이트
                        await ppeApi.update(existingPPE.id, ppeData);
                    } else {
                        // PPE 데이터가 없는 경우에만 새로 생성
                        await ppeApi.create(project.id, ppeData);
                    }
                    showToast('데이터가 성공적으로 수정되었습니다.', 'success');
                } catch (notFoundError) {
                    // PPE 데이터가 없으면 새로 생성 (첫 번째 PPE 데이터)
                    await ppeApi.create(project.id, ppeData);
                    showToast('데이터가 성공적으로 생성되었습니다.', 'success');
                }
                
                // 기존 프로젝트 편집 후 부모 컴포넌트에 알림 (페이지 새로고침 위해)
                if (typeof onSave === 'function') {
                    onSave({ type: 'update', projectId: project.id });
                }
            } else {
                // 새 프로젝트의 경우, onSave 콜백으로 데이터 전달
                // selectedPM은 이제 Employee ID이므로, 이름과 ID를 함께 전송
                const selectedEmployee = employees.find(emp => emp.id.toString() === selectedPM);

                // 부모 컴포넌트에서 프로젝트 생성 후 PPE 저장 처리
                const projectWithPPE = {
                    projectName: projectData.projectName,
                    period: projectData.period,
                    months: Math.round(projectData.months),
                    pm: selectedEmployee ? selectedEmployee.name : '',
                    pmId: selectedEmployee ? selectedEmployee.id : null,
                    startDate: startDate,
                    endDate: endDate,
                    ppeData: ppeData
                };
                
                // onSave prop이 있으면 호출 (프로젝트 생성용)
                if (typeof onSave === 'function') {
                    await onSave(projectWithPPE as any);
                } else {
                    showToast('프로젝트 정보가 없어서 PPE 데이터만 준비되었습니다.', 'warning');
                }
            }
            
            onClose();
        } catch (error) {
            showToast('PPE 저장 중 오류가 발생했습니다.', 'error');
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-40">
            <div 
                className="absolute inset-0 bg-black bg-opacity-20"
                onClick={handleClose}
            ></div>
            <div className={`absolute top-0 right-0 h-full w-full max-w-6xl bg-white shadow-2xl flex flex-col transform transition-transform duration-300 ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}>
                <header className="p-4 border-b border-slate-200 flex items-center justify-between">
                    <div className="flex-1 mr-4">
                        {project ? (
                            <div className="font-bold text-lg">
                                <EditableCell
                                    value={project.name}
                                    onChange={(v) => {
                                        // 프로젝트 이름 업데이트는 저장 시 처리
                                        handleUpdate(['projectName'], v);
                                    }}
                                    placeholder="프로젝트명 입력"
                                    textAlign="left"
                                    readOnly={readOnly}
                                />
                            </div>
                        ) : (
                            <div className="font-bold text-lg">
                                <EditableCell
                                    value={projectData.projectName}
                                    onChange={(v) => handleUpdate(['projectName'], v)}
                                    placeholder="새 프로젝트명 입력"
                                    textAlign="left"
                                    readOnly={readOnly}
                                />
                            </div>
                        )}
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="text-sm text-slate-600">
                            <span className="font-medium">PM:</span>
                            {readOnly ? (
                                <span className="ml-2 px-2 py-1 text-sm">
                                    {employees.find(emp => emp.id.toString() === selectedPM)?.name || project?.pm || '-'}
                                </span>
                            ) : (
                                <select
                                    value={selectedPM}
                                    onChange={(e) => setSelectedPM(e.target.value)}
                                    disabled={userRole === 'PROJECT_MANAGER'}
                                    className={`ml-2 px-2 py-1 text-sm border border-gray-300 rounded min-w-[150px] ${
                                        userRole === 'PROJECT_MANAGER'
                                            ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                                            : 'bg-white'
                                    }`}
                                >
                                    <option value="">PM 선택</option>
                                    {employees
                                        .filter(emp => emp.status === 'ACTIVE')
                                        .map(emp => (
                                            <option key={emp.id} value={emp.id.toString()}>
                                                {emp.name} ({emp.position})
                                            </option>
                                        ))
                                    }
                                </select>
                            )}
                        </div>
                        <button
                            onClick={handleClose}
                            className="p-2 rounded-full text-slate-500 hover:bg-slate-100 text-2xl"
                        >
                            &times;
                        </button>
                    </div>
                </header>
                
                <div className="flex-1 overflow-y-auto">
                    <div className="bg-white p-4 font-sans text-xs">

                        {/* 프로젝트 기본 정보 바 */}
                        <div className="mb-4 p-3 bg-slate-100 rounded-lg flex flex-wrap items-center gap-4 text-sm">
                            <div className="flex items-center gap-2">
                                <span className="font-medium text-slate-600">기간:</span>
                                {readOnly ? (
                                    <span>{startDate} ~ {endDate}</span>
                                ) : (
                                    <div className="flex items-center gap-1">
                                        <input type="date" value={startDate} onChange={(e) => handleDateChange('start', e.target.value)} className="bg-white  rounded px-2 py-1 text-xs" />
                                        <span>~</span>
                                        <input type="date" value={endDate} onChange={(e) => handleDateChange('end', e.target.value)} className="bg-white  rounded px-2 py-1 text-xs" />
                                    </div>
                                )}
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="font-medium text-slate-600">기간(월):</span>
                                <span className="text-blue-600 font-bold">{projectData.months}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="font-medium text-slate-600">인건비율:</span>
                                <span className="text-blue-600 font-bold">{formatPercentage(totals.manpowerCost / (totals.revenue || 1))}</span>
                                <span className="text-xs text-red-500">※ 65% 미만 권장</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="font-medium text-slate-600">ECM:</span>
                                <span className="text-red-500 font-bold">{formatCurrency(totals.ecm)}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="font-medium text-slate-600">Reserved:</span>
                                <span className="text-red-500 font-bold">{formatCurrency(totals.reserved)}</span>
                            </div>
                        </div>

                        <div className="mb-2 text-right text-xs text-gray-600">(단위 : 만원)</div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-xs border-collapse ">
                                <thead>
                                    <tr className="bg-slate-700 text-white font-bold">
                                        <th className="p-2  w-28">Category</th>
                                        <th className="p-2  w-36">Item</th>
                                        <th className="p-2  w-40">Contents</th>
                                        <th className="p-2  w-20">기간/비율</th>
                                        <th className="p-2  w-24">금액</th>
                                        <th className="p-2  w-24">비고</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {/* ========== Revenue Section ========== */}
                                    {/* Revenue 합계 헤더 */}
                                    <tr className="bg-slate-200 font-bold">
                                        <td className="p-2">Revenue</td>
                                        <td className="p-2 "></td>
                                        <td className="p-2 "></td>
                                        <td className="p-2  text-center"></td>
                                        <td className="p-2  text-right">{formatCurrency(totals.revenue)}</td>
                                        <td className="p-2  text-center text-xs text-gray-500">입금예정일</td>
                                    </tr>

                                    {/* Revenue by Schedule 섹션 */}
                                    <tr className="bg-slate-100">
                                        <td className="p-2 "></td>
                                        <td className="p-2  font-semibold">by Schedule</td>
                                        <td className="p-2 "></td>
                                        <td className="p-2  text-center"></td>
                                        <td className="p-2  text-right font-semibold">
                                            {formatCurrency(projectData.revenue.items.reduce((sum, item) => sum + (Number(item.amount) || 0), 0))}
                                        </td>
                                        <td className="p-2 "></td>
                                    </tr>
                                    {projectData.revenue.items.map((item, i) => {
                                        const itemRatio = totals.revenue > 0 ? item.amount / totals.revenue : 0;
                                        return (
                                            <tr key={`schedule-${i}`} className="hover:bg-gray-50">
                                                <td className="p-2 "></td>
                                                <td className="p-2 "></td>
                                                <td className="p-2 ">
                                                    {readOnly ? (
                                                        <span className="px-2 py-1">{item.name}</span>
                                                    ) : (
                                                        <div className="flex items-center gap-2">
                                                            <input
                                                                type="text"
                                                                value={item.name}
                                                                onChange={(e) => handleUpdate(['revenue', 'items', i, 'name'], e.target.value)}
                                                                placeholder="항목명"
                                                                className="flex-1 bg-white border border-indigo-300 rounded px-2 py-1 text-xs"
                                                            />
                                                            {projectData.revenue.items.length > 1 && (
                                                                <button
                                                                    onClick={() => {
                                                                        const newItems = projectData.revenue.items.filter((_, index) => index !== i);
                                                                        handleUpdate(['revenue', 'items'], newItems);
                                                                    }}
                                                                    className="text-red-500 hover:text-red-700 text-lg font-bold"
                                                                    title="삭제"
                                                                >×</button>
                                                            )}
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="p-2  text-center">{formatPercentage(itemRatio)}</td>
                                                <td className="p-2  text-right">
                                                    <EditableCell
                                                        value={item.amount}
                                                        onChange={(v) => handleUpdate(['revenue', 'items', i, 'amount'], Number(v) || 0)}
                                                        isNumber
                                                        readOnly={readOnly}
                                                    />
                                                </td>
                                                <td className="p-2  text-center">
                                                    {readOnly ? (
                                                        <span>{item.dueDate || '-'}</span>
                                                    ) : (
                                                        <input
                                                            type="date"
                                                            value={item.dueDate || ''}
                                                            onChange={(e) => handleUpdate(['revenue', 'items', i, 'dueDate'], e.target.value)}
                                                            className="w-28 bg-white border border-indigo-300 rounded px-1 py-0.5 text-xs"
                                                        />
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                    {!readOnly && (
                                        <tr>
                                            <td className="p-1 " colSpan={6}>
                                                <button
                                                    onClick={() => {
                                                        const suggestedNames = ['착수금', '중도금', '잔금', '추가금', '기타'];
                                                        const usedNames = projectData.revenue.items.map(item => item.name);
                                                        const availableName = suggestedNames.find(name => !usedNames.includes(name)) || `항목 ${projectData.revenue.items.length + 1}`;
                                                        const newItem = { name: availableName, ratio: 0, amount: 0, dueDate: '' };
                                                        handleUpdate(['revenue', 'items'], [...projectData.revenue.items, newItem]);
                                                    }}
                                                    className="w-full py-1 text-indigo-600 hover:bg-indigo-50 rounded text-xs font-medium"
                                                >
                                                    + Schedule 항목 추가
                                                </button>
                                            </td>
                                        </tr>
                                    )}

                                    {/* Revenue by Consultant 섹션 */}
                                    <tr className="bg-slate-100">
                                        <td className="p-2 "></td>
                                        <td className="p-2  font-semibold">by Consultant</td>
                                        <td className="p-2  text-xs text-gray-500">이름 / M/M</td>
                                        <td className="p-2  text-center text-xs text-gray-500">매출원가</td>
                                        <td className="p-2  text-right font-semibold">
                                            {formatCurrency(projectData.revenueByConsultant.items.reduce((sum, item) => sum + Math.round((item.unitCost || 0) * (item.mm || 0)), 0))}
                                        </td>
                                        <td className="p-2  text-center text-xs text-gray-500"></td>
                                    </tr>
                                    {!project && (
                                        <tr className="bg-slate-50">
                                            <td className="p-2" colSpan={6}>
                                                <div className="flex items-center gap-2 text-xs">
                                                    <svg className="w-4 h-4 text-slate-500" fill="currentColor" viewBox="0 0 20 20">
                                                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                                    </svg>
                                                    <span className="text-slate-600">프로젝트 저장 후 수정 모드에서 인력을 추가하세요.</span>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                    {projectData.revenueByConsultant.items.map((item, i) => {
                                        const unitCost = item.unitCost || 0;

                                        return (
                                            <tr key={`consultant-${i}`} className="hover:bg-gray-50">
                                                <td className="p-2 "></td>
                                                <td className="p-2 "></td>
                                                <td className="p-2 ">
                                                    {readOnly ? (
                                                        <span>{item.name} / {item.mm || 0}</span>
                                                    ) : (
                                                        <div className="flex items-center gap-1">
                                                            <input
                                                                type="text"
                                                                list={`consultant-list-${i}`}
                                                                value={item.name}
                                                                placeholder="이름"
                                                                onChange={(e) => {
                                                                    const selectedEmployee = employees.find(emp => emp.name === e.target.value);
                                                                    handleUpdate(['revenueByConsultant', 'items', i, 'name'], e.target.value);
                                                                    handleUpdate(['revenueByConsultant', 'items', i, 'employeeId'], selectedEmployee?.id || null);
                                                                }}
                                                                className="flex-1 bg-white border border-indigo-300 rounded px-1 py-0.5 text-xs min-w-0"
                                                            />
                                                            <datalist id={`consultant-list-${i}`}>
                                                                {employees.filter(emp => emp.status === 'ACTIVE').map(emp => (
                                                                    <option key={emp.id} value={emp.name}>{emp.name}</option>
                                                                ))}
                                                            </datalist>
                                                            <span className="w-12 text-center text-xs text-gray-600">{item.mm || 0}</span>
                                                            <button
                                                                onClick={() => {
                                                                    const deletedItem = projectData.revenueByConsultant.items[i];
                                                                    setProjectData(prev => {
                                                                        const newProject = JSON.parse(JSON.stringify(prev));
                                                                        // Revenue by Consultant 항목 삭제
                                                                        newProject.revenueByConsultant.items = newProject.revenueByConsultant.items.filter((_: any, index: number) => index !== i);
                                                                        // 연결된 투입인건비 항목도 삭제
                                                                        if (deletedItem?.id) {
                                                                            newProject.manpowerCost.items = newProject.manpowerCost.items.filter((mp: any) => mp.revenueByConsultantId !== deletedItem.id);
                                                                        }
                                                                        return newProject;
                                                                    });
                                                                }}
                                                                className="text-red-500 hover:text-red-700 font-bold"
                                                            >×</button>
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="p-2 text-center">
                                                    {readOnly ? (
                                                        <span>{formatCurrency(unitCost)}</span>
                                                    ) : (
                                                        <input
                                                            type="number"
                                                            step="100"
                                                            value={unitCost || ''}
                                                            onChange={(e) => handleUpdate(['revenueByConsultant', 'items', i, 'unitCost'], parseFloat(e.target.value) || 0)}
                                                            placeholder="0"
                                                            className="w-16 text-center bg-white border border-indigo-300 rounded px-1 py-0.5 text-xs [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                                        />
                                                    )}
                                                </td>
                                                <td className="p-2 text-right font-semibold">
                                                    {formatCurrency(Math.round((item.unitCost || 0) * (item.mm || 0)))}
                                                </td>
                                                <td className="p-2 text-center">
                                                    {item.name && !readOnly && (
                                                        <button
                                                            onClick={() => {
                                                                setSelectedManpowerEmployeeIndex(i);
                                                                setIsManpowerForRevenueByConsultant(true);
                                                                setIsManpowerModalOpen(true);
                                                            }}
                                                            className="px-2 py-0.5 text-xs bg-blue-500 text-white rounded hover:bg-blue-600"
                                                            title="월별 투입 경과"
                                                        >투입경과</button>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                    {project?.id && !readOnly && (
                                        <tr>
                                            <td className="p-1 " colSpan={6}>
                                                <button
                                                    onClick={() => {
                                                        const newItem = { id: `rbc_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`, name: '', employeeId: null, mm: 0, unitCost: 0, monthlyInputs: Array(12).fill(0) };
                                                        handleUpdate(['revenueByConsultant', 'items'], [...projectData.revenueByConsultant.items, newItem]);
                                                    }}
                                                    className="w-full py-1 text-slate-600 hover:bg-slate-50 rounded text-xs font-medium"
                                                >
                                                    + Consultant 추가
                                                </button>
                                            </td>
                                        </tr>
                                    )}

                                    {/* CoGs Section */}
                                    <tr className="bg-slate-200 font-bold">
                                        <td className="p-2">CoGs</td>
                                        <td className="p-2"></td>
                                        <td className="p-2 text-xs text-gray-500">MM × 단가</td>
                                        <td className="p-2 text-center text-xs text-gray-500">M/M</td>
                                        <td className="p-2 text-right">{formatCurrency(totals.cogs + totals.manpowerCost)}</td>
                                        <td className="p-2"></td>
                                    </tr>

                                    {/* 투입인건비 - CoGs Item */}
                                    <tr className="bg-slate-100">
                                        <td className="p-2"></td>
                                        <td className="p-2 font-semibold">투입인건비</td>
                                        <td className="p-2 text-xs text-gray-500">이름 / M/M</td>
                                        <td className="p-2 text-center text-xs text-gray-500">인력원가</td>
                                        <td className="p-2 text-right font-semibold">{formatCurrency(totals.manpowerCost)}</td>
                                        <td className="p-2 text-center text-xs text-gray-500"></td>
                                    </tr>
                                    {!project && (
                                        <tr className="bg-slate-50">
                                            <td className="p-2" colSpan={6}>
                                                <div className="flex items-center gap-2 text-xs">
                                                    <svg className="w-4 h-4 text-slate-500" fill="currentColor" viewBox="0 0 20 20">
                                                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                                    </svg>
                                                    <span className="text-slate-600">프로젝트 저장 후 수정 모드에서 인력을 추가하세요.</span>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                    {projectData.manpowerCost.items.map((item, i) => {
                                        const unitCost = item.unitCost || 0;
                                        const totalAmount = Math.round((item.mm || 0) * unitCost);
                                        return (
                                            <tr key={`manpower-${i}`} className="hover:bg-gray-50">
                                                <td className="p-2"></td>
                                                <td className="p-2"></td>
                                                <td className="p-2">
                                                    {readOnly ? (
                                                        <span>{item.name} / {item.mm || 0}</span>
                                                    ) : (
                                                        <div className="flex items-center gap-1">
                                                            <input
                                                                type="text"
                                                                list={`employee-list-${i}`}
                                                                value={item.name}
                                                                placeholder="이름"
                                                                onChange={async (e) => {
                                                                    const selectedEmployee = employees.find(emp => emp.name === e.target.value && emp.status === 'ACTIVE');
                                                                    if (selectedEmployee && selectedEmployee.role === 'DEVELOPER' && userRole !== 'DEVELOPER') return;
                                                                    handleUpdate(['manpowerCost', 'items', i, 'name'], e.target.value);
                                                                    handleUpdate(['manpowerCost', 'items', i, 'employeeId'], selectedEmployee?.id || null);
                                                                    if (!item.monthlyInputs) handleUpdate(['manpowerCost', 'items', i, 'monthlyInputs'], Array(12).fill(0));
                                                                    if (selectedEmployee) {
                                                                        const empWithCost = selectedEmployee as any;
                                                                        if (empWithCost.weightedAvgUnitCost !== undefined) {
                                                                            handleUpdate(['manpowerCost', 'items', i, 'unitCost'], empWithCost.weightedAvgUnitCost);
                                                                        }
                                                                    }
                                                                }}
                                                                className="flex-1 bg-white border border-indigo-300 rounded px-1 py-0.5 text-xs min-w-0"
                                                            />
                                                            <datalist id={`employee-list-${i}`}>
                                                                {employees.filter(emp => emp.status === 'ACTIVE').map(emp => (
                                                                    <option key={emp.id} value={emp.name}>{emp.name}</option>
                                                                ))}
                                                            </datalist>
                                                            <span className="w-12 text-center text-xs text-gray-600">{item.mm || 0}</span>
                                                            <button
                                                                onClick={() => handleUpdate(['manpowerCost', 'items'], projectData.manpowerCost.items.filter((_, idx) => idx !== i))}
                                                                className="text-red-500 hover:text-red-700 font-bold"
                                                            >×</button>
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="p-2 text-center">
                                                    {readOnly ? (
                                                        <span>{formatCurrency(unitCost)}</span>
                                                    ) : (
                                                        <input
                                                            type="number"
                                                            step="100"
                                                            value={unitCost || ''}
                                                            onChange={(e) => handleUpdate(['manpowerCost', 'items', i, 'unitCost'], parseFloat(e.target.value) || 0)}
                                                            placeholder="0"
                                                            className="w-16 text-center bg-white border border-indigo-300 rounded px-1 py-0.5 text-xs [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                                        />
                                                    )}
                                                </td>
                                                <td className="p-2 text-right font-semibold">
                                                    {formatCurrency(totalAmount)}
                                                </td>
                                                <td className="p-2 text-center">
                                                    {item.name && !readOnly && (
                                                        <button
                                                            onClick={() => {
                                                                setSelectedManpowerEmployeeIndex(i);
                                                                setIsManpowerForRevenueByConsultant(false);
                                                                setIsManpowerModalOpen(true);
                                                            }}
                                                            className="px-2 py-0.5 text-xs bg-green-500 text-white rounded hover:bg-green-600"
                                                            title="월별 투입 경과"
                                                        >투입경과</button>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                    {project?.id && !readOnly && (
                                        <tr>
                                            <td className="p-1" colSpan={6}>
                                                <button
                                                    onClick={() => handleUpdate(['manpowerCost', 'items'], [...projectData.manpowerCost.items, { name: '', employeeId: null, mm: 0, unitCost: 0, monthlyInputs: Array(12).fill(0) }])}
                                                    className="w-full py-1 text-slate-600 hover:bg-slate-50 rounded text-xs font-medium"
                                                >+ 인력 추가</button>
                                            </td>
                                        </tr>
                                    )}

                                    {/* 프리랜서 - CoGs Item */}
                                    <tr className="bg-slate-100">
                                        <td className="p-2"></td>
                                        <td className="p-2 font-semibold">프리랜서</td>
                                        <td className="p-2 text-xs text-gray-500">MM × 단가</td>
                                        <td className="p-2 text-center text-xs text-gray-500">M/M</td>
                                        <td className="p-2 text-right font-semibold">{formatCurrency(totals.cogs)}</td>
                                        <td className="p-2 text-center"></td>
                                    </tr>
                                    {projectData.cogs.items.map((item, i) => {
                                        const isFreelancerSenior = item.name.includes('프리랜서 고급');
                                        const isFreelancerMiddle = item.name.includes('프리랜서 중급');
                                        const isFreelancer = isFreelancerSenior || isFreelancerMiddle;
                                        const unitPrice = (item as any).unitPrice || (isFreelancerSenior ? 1800 : 1500);

                                        let mmValue = 0;
                                        let displayAmount = item.amount;

                                        if (isFreelancer && (item as any).monthlyInputs && Array.isArray((item as any).monthlyInputs)) {
                                            mmValue = (item as any).monthlyInputs.reduce((sum: number, val: number) => sum + (Number(val) || 0), 0);
                                            displayAmount = Math.round(mmValue * unitPrice);
                                        } else if (isFreelancer) {
                                            mmValue = item.amount / unitPrice;
                                        }

                                        return (
                                            <tr key={`cogs-${i}`} className="hover:bg-gray-50">
                                                <td className="p-2 "></td>
                                                <td className="p-2 "></td>
                                                <td className="p-2 ">
                                                    {item.name}
                                                </td>
                                                <td className="p-2  text-center">
                                                    {isFreelancer && mmValue > 0 ? mmValue.toFixed(1) : ''}
                                                </td>
                                                <td className="p-2  text-right">
                                                    {isFreelancer ? (
                                                        formatCurrency(displayAmount)
                                                    ) : (
                                                        <EditableCell
                                                            value={item.amount}
                                                            onChange={(v) => handleUpdate(['cogs', 'items', i, 'amount'], Number(v) || 0)}
                                                            isNumber
                                                            readOnly={readOnly}
                                                        />
                                                    )}
                                                </td>
                                                <td className="p-2 text-center">
                                                    {isFreelancer && !readOnly && (
                                                        <button
                                                            onClick={() => {
                                                                setSelectedFreelancerIndex(i);
                                                                setIsFreelancerModalOpen(true);
                                                            }}
                                                            className="px-2 py-0.5 text-xs bg-orange-500 text-white rounded hover:bg-orange-600"
                                                        >투입경과</button>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}

                                    {/* Gross Margin */}
                                    <tr className="bg-slate-200 font-bold">
                                        <td className="p-2">Gross Margin</td>
                                        <td className="p-2"></td>
                                        <td className="p-2"></td>
                                        <td className="p-2 text-center">{formatPercentage(totals.grossMargin / (totals.revenue || 1))}</td>
                                        <td className="p-2 text-right">{formatCurrency(totals.grossMargin)}</td>
                                        <td className="p-2"></td>
                                    </tr>

                                    {/* SG&A Section */}
                                    <tr className="bg-slate-200 font-bold">
                                        <td className="p-2">SG&A</td>
                                        <td className="p-2 "></td>
                                        <td className="p-2 "></td>
                                        <td className="p-2  text-center">{formatPercentage((totals.indirectOpexPercentage + totals.directOpexPercentage))}</td>
                                        <td className="p-2  text-right">{formatCurrency(totals.indirectOpex + totals.directOpex)}</td>
                                        <td className="p-2 "></td>
                                    </tr>

                                    {/* Indirect Opex */}
                                    <tr className="bg-slate-100">
                                        <td className="p-2 "></td>
                                        <td className="p-2  font-semibold">Indirect Opex</td>
                                        <td className="p-2 "></td>
                                        <td className="p-2  text-center">{formatPercentage(totals.indirectOpexPercentage)}</td>
                                        <td className="p-2  text-right">{formatCurrency(totals.indirectOpex)}</td>
                                        <td className="p-2 "></td>
                                    </tr>
                                    {projectData.indirectOpex.items.map((item, i) => (
                                        <tr key={`indirect-${i}`} className="hover:bg-gray-50">
                                            <td className="p-2 "></td>
                                            <td className="p-2 "></td>
                                            <td className="p-2 ">{item.name}</td>
                                            <td className="p-2  text-center">
                                                {item.name.includes('Cost of Finance') ? (
                                                    readOnly ? formatPercentage(projectData.costOfFinanceRate || 0.1) : (
                                                        <div className="flex items-center justify-center">
                                                            <input
                                                                type="number"
                                                                step="0.1"
                                                                value={((projectData.costOfFinanceRate || 0.1) * 100).toFixed(1)}
                                                                onChange={(e) => handleUpdate(['costOfFinanceRate'], Math.min(Math.max(Number(e.target.value) / 100, 0), 1))}
                                                                className="w-12 text-right bg-white border border-indigo-300 rounded px-1 py-0.5 text-xs [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                                            />
                                                            <span className="ml-0.5 text-xs">%</span>
                                                        </div>
                                                    )
                                                ) : formatPercentage((item.amount || 0) / (totals.revenue || 1))}
                                            </td>
                                            <td className="p-2  text-right">
                                                {item.name.includes('Cost of Finance') ? formatCurrency(totals.costOfFinance) : (
                                                    <EditableCell value={item.amount} onChange={(v) => handleUpdate(['indirectOpex', 'items', i, 'amount'], Number(v) || 0)} isNumber readOnly={readOnly} />
                                                )}
                                            </td>
                                            <td className="p-2  text-xs text-red-400">
                                                {item.name.includes('Cost of Finance') && '※ 10% 기본'}
                                            </td>
                                        </tr>
                                    ))}

                                    {/* Direct Opex */}
                                    <tr className="bg-slate-100">
                                        <td className="p-2 "></td>
                                        <td className="p-2  font-semibold">Direct Opex</td>
                                        <td className="p-2 "></td>
                                        <td className="p-2  text-center">{formatPercentage(totals.directOpexPercentage)}</td>
                                        <td className="p-2  text-right">{formatCurrency(totals.directOpex)}</td>
                                        <td className="p-2 "></td>
                                    </tr>
                                    {projectData.directOpex.items.map((item, i) => (
                                        <tr key={`direct-${i}`} className="hover:bg-gray-50">
                                            <td className="p-2 "></td>
                                            <td className="p-2 "></td>
                                            <td className="p-2 ">{item.name}</td>
                                            <td className="p-2  text-center">
                                                {item.name.includes('Project management') ? (
                                                    readOnly ? formatPercentage(projectData.projectManagementRate || 0.03) : (
                                                        <div className="flex items-center justify-center">
                                                            <input
                                                                type="number"
                                                                step="0.1"
                                                                value={((projectData.projectManagementRate || 0.03) * 100).toFixed(1)}
                                                                onChange={(e) => handleUpdate(['projectManagementRate'], Math.min(Math.max(Number(e.target.value) / 100, 0), 1))}
                                                                className="w-12 text-right bg-white border border-indigo-300 rounded px-1 py-0.5 text-xs [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                                            />
                                                            <span className="ml-0.5 text-xs">%</span>
                                                        </div>
                                                    )
                                                ) : formatPercentage((item.amount || 0) / (totals.revenue || 1))}
                                            </td>
                                            <td className="p-2  text-right">
                                                {item.name.includes('Project management') ? formatCurrency(totals.projectManagement) : (
                                                    <EditableCell value={item.amount} onChange={(v) => handleUpdate(['directOpex', 'items', i, 'amount'], Number(v) || 0)} isNumber readOnly={readOnly} />
                                                )}
                                            </td>
                                            <td className="p-2  text-xs text-red-400">
                                                {item.name.includes('Project management') && '※ 3% 기본'}
                                            </td>
                                        </tr>
                                    ))}

                                    {/* Summaries */}
                                    <tr className="font-bold bg-slate-200">
                                        <td className="p-2">Operation Margin</td>
                                        <td className="p-2"></td>
                                        <td className="p-2"></td>
                                        <td className="p-2 text-center">{formatPercentage(totals.operationMargin / (totals.revenue || 1))}</td>
                                        <td className="p-2 text-right">{formatCurrency(totals.operationMargin)}</td>
                                        <td className="p-2"></td>
                                    </tr>
                                    <tr className="bg-slate-200">
                                        <td className="p-2"></td>
                                        <td className="p-2">Income Tax (Est. 20%)</td>
                                        <td className="p-2"></td>
                                        <td className="p-2 text-center">{formatPercentage(totals.incomeTax / (totals.revenue || 1))}</td>
                                        <td className="p-2 text-right">{formatCurrency(totals.incomeTax)}</td>
                                        <td className="p-2"></td>
                                    </tr>
                                    <tr className="font-bold bg-slate-800 text-white">
                                        <td className="p-2">Net Profit</td>
                                        <td className="p-2"></td>
                                        <td className="p-2"></td>
                                        <td className="p-2 text-center">{formatPercentage(totals.netProfit / (totals.revenue || 1))}</td>
                                        <td className="p-2 text-right">{formatCurrency(totals.netProfit)}</td>
                                        <td className="p-2 text-xs text-red-400">※ EP 10% 미만 불가</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                <footer className="p-4 bg-slate-50 flex justify-end space-x-2">
                    <button
                        onClick={handleClose}
                        className="px-4 py-2 text-sm font-semibold text-slate-600 bg-white border-slate-300 rounded-lg hover:bg-slate-50"
                    >
                        닫기
                    </button>
                    {!readOnly && (
                        <button
                            onClick={handleSave}
                            className="px-4 py-2 text-sm font-semibold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700"
                        >
                            {project ? '수정' : '저장'}
                        </button>
                    )}
                </footer>
            </div>
            
            {/* 내부 직원 투입 경과 모달 (월별 M/M 입력) */}
            <ManpowerInputModal
                isOpen={isManpowerModalOpen}
                onClose={() => {
                    setIsManpowerModalOpen(false);
                    setSelectedManpowerEmployeeIndex(-1);
                }}
                employeeName={
                    isManpowerForRevenueByConsultant
                        ? projectData.revenueByConsultant.items[selectedManpowerEmployeeIndex]?.name || ''
                        : projectData.manpowerCost.items[selectedManpowerEmployeeIndex]?.name || ''
                }
                employeeData={
                    isManpowerForRevenueByConsultant
                        ? {
                            name: projectData.revenueByConsultant.items[selectedManpowerEmployeeIndex]?.name || '',
                            employeeId: projectData.revenueByConsultant.items[selectedManpowerEmployeeIndex]?.employeeId,
                            mm: projectData.revenueByConsultant.items[selectedManpowerEmployeeIndex]?.mm || 0,
                            unitCost: projectData.revenueByConsultant.items[selectedManpowerEmployeeIndex]?.unitCost || 0,
                            monthlyInputs: projectData.revenueByConsultant.items[selectedManpowerEmployeeIndex]?.monthlyInputs
                        }
                        : {
                            name: projectData.manpowerCost.items[selectedManpowerEmployeeIndex]?.name || '',
                            employeeId: projectData.manpowerCost.items[selectedManpowerEmployeeIndex]?.employeeId,
                            mm: projectData.manpowerCost.items[selectedManpowerEmployeeIndex]?.mm || 0,
                            unitCost: projectData.manpowerCost.items[selectedManpowerEmployeeIndex]?.unitCost || 0,
                            monthlyInputs: projectData.manpowerCost.items[selectedManpowerEmployeeIndex]?.monthlyInputs
                        }
                }
                onSave={(updatedData) => {
                    if (isManpowerForRevenueByConsultant) {
                        // Revenue by Consultant 항목 업데이트 + CoGs 투입인건비 자동 반영
                        const currentItem = projectData.revenueByConsultant.items[selectedManpowerEmployeeIndex];

                        setProjectData(prev => {
                            const newProject = JSON.parse(JSON.stringify(prev));

                            // 1. Revenue by Consultant 업데이트
                            newProject.revenueByConsultant.items = newProject.revenueByConsultant.items.map((item: any, idx: number) => {
                                if (idx === selectedManpowerEmployeeIndex) {
                                    return {
                                        ...item,
                                        mm: updatedData.mm,
                                        monthlyInputs: updatedData.monthlyInputs
                                    };
                                }
                                return item;
                            });

                            // 2. CoGs 투입인건비 자동 반영 (revenueByConsultantId로 매칭)
                            if (currentItem && currentItem.name) {
                                // Revenue by Consultant 항목에 ID가 없으면 생성
                                const itemId = currentItem.id || `rbc_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
                                if (!currentItem.id) {
                                    // Revenue by Consultant 항목에 ID 추가
                                    newProject.revenueByConsultant.items[selectedManpowerEmployeeIndex].id = itemId;
                                }

                                // 직원의 인력원가(weightedAvgUnitCost) 찾기
                                const emp = employees.find((e: any) =>
                                    e.id === currentItem.employeeId || e.name === currentItem.name
                                ) as any;
                                const unitCost = emp?.weightedAvgUnitCost || 0;

                                // revenueByConsultantId로 매칭
                                const existingIndex = newProject.manpowerCost.items.findIndex(
                                    (mp: any) => mp.revenueByConsultantId === itemId
                                );

                                if (existingIndex >= 0) {
                                    // 기존 항목 업데이트 (mm, monthlyInputs만 - 이름, unitCost는 유지)
                                    newProject.manpowerCost.items[existingIndex] = {
                                        ...newProject.manpowerCost.items[existingIndex],
                                        mm: updatedData.mm,
                                        monthlyInputs: updatedData.monthlyInputs
                                    };
                                } else {
                                    // 새 항목 추가 (revenueByConsultantId 포함)
                                    newProject.manpowerCost.items.push({
                                        name: currentItem.name,
                                        employeeId: currentItem.employeeId,
                                        unitCost: unitCost,
                                        mm: updatedData.mm,
                                        monthlyInputs: updatedData.monthlyInputs,
                                        revenueByConsultantId: itemId
                                    });
                                }
                            }

                            return newProject;
                        });
                    } else {
                        // 투입인건비 항목 업데이트
                        const updatedItems = projectData.manpowerCost.items.map((item, idx) => {
                            if (idx === selectedManpowerEmployeeIndex) {
                                return {
                                    ...item,
                                    mm: updatedData.mm,
                                    monthlyInputs: updatedData.monthlyInputs
                                };
                            }
                            return item;
                        });
                        handleUpdate(['manpowerCost', 'items'], updatedItems);
                    }
                }}
                startDate={startDate}
                endDate={endDate}
            />
            
            {/* 프리랜서 투입 경과 모달 */}
            <FreelancerInputModal
                isOpen={isFreelancerModalOpen}
                onClose={() => {
                    setIsFreelancerModalOpen(false);
                    setSelectedFreelancerIndex(-1);
                }}
                freelancerItems={selectedFreelancerIndex >= 0 ? [projectData.cogs.items[selectedFreelancerIndex]] : []}
                freelancerName={selectedFreelancerIndex >= 0 ? projectData.cogs.items[selectedFreelancerIndex]?.name : ''}
                startDate={startDate}
                endDate={endDate}
                onSave={(items) => {
                    // 선택된 프리랜서만 업데이트
                    if (selectedFreelancerIndex >= 0 && items.length > 0) {
                        const updatedItem = items[0];
                        const updatedCogsItems = [...projectData.cogs.items];
                        updatedCogsItems[selectedFreelancerIndex] = {
                            ...updatedCogsItems[selectedFreelancerIndex],
                            ...updatedItem
                        };
                        handleUpdate(['cogs', 'items'], updatedCogsItems);
                    }
                }}
            />
        </div>
    );
};

export default NewProjectModal;